import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CHAT_DOCUMENT_MAX_BYTES,
  CHAT_DOCUMENT_MAX_PAGES,
  createChatDocumentUpload,
  deleteChatDocument,
  getChatDocument,
  isAcceptedChatDocument,
  prepareChatDocument,
  uploadChatDocument,
  type ChatDocumentDto,
} from '@/services/chatDocuments';
import { countPdfPages } from '@/services/pdfPageCount';

const STATUS_POLL_INTERVAL_MS = 5_000;
const STATUS_POLL_ATTEMPTS = 30;

export interface PendingDocument extends Omit<ChatDocumentDto, 'status'> {
  localId: string;
  file: File;
  /** Draft-only state; the source bytes have reached the signed upload URL. */
  isUploaded: boolean;
  status: 'uploading' | ChatDocumentDto['status'];
}

/**
 * Holds document source bytes locally until send. A prepared document has a
 * server ID, but never enters Redux until the chat turn is created.
 */
export const usePendingDocuments = (options: {
  disabled: boolean;
  available: () => number;
  notify: (message: string) => void;
}) => {
  const [pending, setPending] = useState<PendingDocument[]>([]);
  const ref = useRef(pending);
  const optionsRef = useRef(options);
  const uploadControllers = useRef(new Map<string, AbortController>());
  const pollTimers = useRef(new Map<string, number>());
  const cleanupInFlight = useRef(new Set<string>());

  const pollRef = useRef<
    (localId: string, id: string, attempt: number) => void
  >(() => undefined);

  optionsRef.current = options;

  const update = useCallback((next: PendingDocument[]) => {
    ref.current = next;
    setPending(next);
  }, []);

  const patch = useCallback(
    (localId: string, fields: Partial<PendingDocument>) => {
      update(
        ref.current.map((item) =>
          item.localId === localId ? { ...item, ...fields } : item
        )
      );
    },
    [update]
  );

  const clearPoll = useCallback((localId: string) => {
    const timer = pollTimers.current.get(localId);
    if (timer !== undefined) window.clearTimeout(timer);
    pollTimers.current.delete(localId);
  }, []);

  // A remove, unmount, and late PUT completion can all observe the same
  // draft. Coalesce only concurrent cleanup. A failed DELETE is eligible for
  // the next lifecycle cleanup, while expected 404/409 responses stay quiet.
  const cleanupDraft = useCallback((id: string) => {
    if (cleanupInFlight.current.has(id)) return;
    cleanupInFlight.current.add(id);

    void Promise.resolve(deleteChatDocument(id))
      .catch(() => undefined)
      .finally(() => cleanupInFlight.current.delete(id));
  }, []);

  const poll = useCallback(
    async (localId: string, id: string, attempt: number) => {
      if (!ref.current.some((item) => item.localId === localId)) return;

      try {
        const document = await getChatDocument(id);
        if (!ref.current.some((item) => item.localId === localId)) return;
        patch(localId, document);

        if (document.status !== 'processing') {
          clearPoll(localId);
          return;
        }

        if (attempt >= STATUS_POLL_ATTEMPTS) {
          patch(localId, {
            status: 'error',
            errorMessage:
              'Preparation is taking longer than expected. Check status again.',
          });

          clearPoll(localId);
          return;
        }

        const timer = window.setTimeout(
          () => pollRef.current(localId, id, attempt + 1),
          STATUS_POLL_INTERVAL_MS
        );

        pollTimers.current.set(localId, timer);
      } catch {
        if (!ref.current.some((item) => item.localId === localId)) return;

        patch(localId, {
          status: 'error',
          errorMessage: 'Ora could not check this document. Try again.',
        });

        clearPoll(localId);
      }
    },
    [clearPoll, patch]
  );

  pollRef.current = (localId, id, attempt) => void poll(localId, id, attempt);

  const prepare = useCallback(
    async (localId: string) => {
      let item = ref.current.find((row) => row.localId === localId);
      if (!item) return;

      const exists = () => ref.current.some((row) => row.localId === localId);

      try {
        // A failed PUT has no reusable signed URL. Delete its draft and issue
        // a fresh upload rather than repeatedly preparing missing source bytes.
        if (!item.id || !item.isUploaded) {
          if (item.id) cleanupDraft(item.id);

          patch(localId, {
            id: '',
            isUploaded: false,
            status: 'uploading',
            errorMessage: null,
            preparationWarning: null,
          });

          // Refuse an over-long PDF before it uses an upload. The server
          // checks again, so an uncertain count (null) proceeds.
          const pageCount = await countPdfPages(item.file);
          if (!exists()) return;
          if (pageCount !== null && pageCount > CHAT_DOCUMENT_MAX_PAGES) {
            const message = `This document has ${pageCount} pages. Documents can have at most ${CHAT_DOCUMENT_MAX_PAGES} pages. Split the file and try again.`;
            patch(localId, { status: 'error', errorMessage: message });
            optionsRef.current.notify(message);
            return;
          }

          const upload = await createChatDocumentUpload(item.file);
          if (!exists()) {
            cleanupDraft(upload.document.id);
            return;
          }

          patch(localId, {
            ...upload.document,
            isUploaded: false,
            status: 'uploading',
          });

          const controller = new AbortController();
          uploadControllers.current.set(localId, controller);
          try {
            await uploadChatDocument(
              upload.uploadUrl,
              item.file,
              upload.document.mimeType,
              controller.signal
            );
          } finally {
            uploadControllers.current.delete(localId);
          }

          if (!exists()) {
            cleanupDraft(upload.document.id);
            return;
          }
          patch(localId, { isUploaded: true, status: 'processing' });
          item = ref.current.find((row) => row.localId === localId);
          if (!item) return;
        } else {
          patch(localId, { status: 'processing', errorMessage: null });
        }

        const document = await prepareChatDocument(item.id);
        if (!exists()) return;
        patch(localId, document);
        if (document.status === 'processing') {
          void poll(localId, item.id, 0);
        }
      } catch (error) {
        const current = ref.current.find((row) => row.localId === localId);
        if (!current) return;

        // A second prepare request found a worker already running. Its status
        // is authoritative, so poll it instead of showing a false failure.
        if (
          current.id &&
          (error as { response?: { status?: number } }).response?.status === 409
        ) {
          void poll(localId, current.id, 0);
          return;
        }

        if ((error as { name?: string }).name === 'AbortError') return;

        const response = (
          error as {
            response?: { status?: number; data?: { error?: unknown } };
          }
        ).response;

        const message =
          [400, 413, 429].includes(response?.status ?? 0) &&
          typeof response?.data?.error === 'string'
            ? response.data.error.slice(0, 300)
            : 'Ora could not prepare this document. Try again.';

        patch(localId, {
          status: 'error',
          errorMessage: message,
        });

        optionsRef.current.notify(message);
      }
    },
    [cleanupDraft, patch, poll]
  );

  const add = useCallback(
    (files: FileList | File[]) => {
      if (optionsRef.current.disabled) return;

      const accepted = Array.from(files)
        .filter((file) => {
          if (!isAcceptedChatDocument(file)) {
            optionsRef.current.notify(
              'Attach a PDF, PowerPoint, Word, TXT, or CSV file.'
            );

            return false;
          }
          if (file.size > CHAT_DOCUMENT_MAX_BYTES) {
            optionsRef.current.notify(
              'Each document must be 20 MB or smaller.'
            );

            return false;
          }
          return true;
        })
        .slice(0, optionsRef.current.available());

      if (!accepted.length) return;

      const additions = accepted.map((file) => ({
        localId: crypto.randomUUID(),
        file,
        id: '',
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        status: 'pending' as const,
        isUploaded: false,
        pageCount: null,
        errorMessage: null,
        preparationWarning: null,
      }));

      update([...ref.current, ...additions]);
      additions.forEach((item) => void prepare(item.localId));
    },
    [prepare, update]
  );

  const remove = useCallback(
    (localId: string) => {
      const item = ref.current.find((row) => row.localId === localId);
      uploadControllers.current.get(localId)?.abort();
      uploadControllers.current.delete(localId);
      clearPoll(localId);
      if (item?.id) cleanupDraft(item.id);
      update(ref.current.filter((row) => row.localId !== localId));
    },
    [cleanupDraft, clearPoll, update]
  );

  const retry = useCallback(
    (localId: string) => {
      const item = ref.current.find((row) => row.localId === localId);
      if (
        !optionsRef.current.disabled &&
        item &&
        ['error', 'processing'].includes(item.status)
      ) {
        clearPoll(localId);
        void prepare(localId);
      }
    },
    [clearPoll, prepare]
  );

  const clear = useCallback(
    (sentIds: readonly string[]) => {
      const sent = new Set(sentIds);

      ref.current.forEach((item) => {
        uploadControllers.current.get(item.localId)?.abort();
        clearPoll(item.localId);
        if (item.id && !sent.has(item.id)) cleanupDraft(item.id);
      });

      update([]);
    },
    [cleanupDraft, clearPoll, update]
  );

  useEffect(
    () => () => {
      const drafts = ref.current;
      // Clear synchronously: later upload/prepare completion deletes a minted
      // draft rather than setting state after its composer disappeared.
      ref.current = [];

      drafts.forEach((item) => {
        uploadControllers.current.get(item.localId)?.abort();
        clearPoll(item.localId);
        if (item.id) cleanupDraft(item.id);
      });
    },
    [cleanupDraft, clearPoll]
  );

  return {
    pending,
    readyIds: pending
      .filter((item) => item.status === 'ready')
      .map((item) => item.id),
    readyAttachments: pending.flatMap((item) =>
      item.status === 'ready'
        ? [
            {
              id: item.id,
              kind: 'document' as const,
              documentId: item.id,
              fileName: item.fileName,
              pageCount: item.pageCount,
              mimeType: item.mimeType,
              sizeBytes: item.sizeBytes,
            },
          ]
        : []
    ),
    isPreparing: pending.some((item) =>
      ['pending', 'uploading', 'processing'].includes(item.status)
    ),
    add,
    remove,
    retry,
    clear,
  };
};
